import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { WidgetContext } from "../../store/widgetContext";
import { defaultTranslations } from "../../localization/defaultTranslations";

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

const getStr = (obj, section, key) => {
	if (!isPlainObject(obj)) return undefined;
	const sec = obj?.[section];
	if (!isPlainObject(sec)) return undefined;
	const val = sec?.[key];
	return typeof val === "string" && val.trim() !== "" ? val : undefined;
};

const formatVars = (template, vars) => {
	if (typeof template !== "string" || template.trim() === "" || !isPlainObject(vars)) return template;
	return template.replace(/\{(\w+)\}/g, (_, k) => {
		const v = vars[k];
		return v === null || v === undefined ? `{${k}}` : String(v);
	});
};

const getBrowserLanguageRaw = () => {
	if (typeof navigator === "undefined") return "en";
	const first =
		Array.isArray(navigator.languages) && navigator.languages.length > 0
			? navigator.languages[0]
			: navigator.language;
	return String(first || "en");
};

const normalizeLang = (rawLang) => {
	return String(rawLang ?? "").trim().toLowerCase().replace(/_/g, "-").split("-")[0] || "en";
};

const resolveWidgetLang = (widget, browserLangRaw) => {
	const browserLang = normalizeLang(browserLangRaw);
	const localesRaw = widget?.meta?.localization?.locales;
	const selected = Array.isArray(localesRaw) ? [...new Set(localesRaw.map(normalizeLang))].filter(Boolean) : [];

	if (selected.length === 1) return selected[0];
	if (selected.length > 1) return selected.includes(browserLang) ? browserLang : selected.includes("en") ? "en" : selected[0];

	return browserLang;
};

const RTL_LANGS = new Set(["ar", "he"]);

export function useWidgetMsg() {
	const { widget } = useContext(WidgetContext);

	const [browserLangRaw, setBrowserLangRaw] = useState(() => getBrowserLanguageRaw());

	useEffect(() => {
		if (typeof window === "undefined") return;

		const update = () => setBrowserLangRaw(getBrowserLanguageRaw());

		window.addEventListener("languagechange", update);
		window.addEventListener("focus", update);

		const onVisibility = () => {
			if (document.visibilityState === "visible") update();
		};
		document.addEventListener("visibilitychange", onVisibility);

		return () => {
			window.removeEventListener("languagechange", update);
			window.removeEventListener("focus", update);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, []);

	const browserLang = useMemo(() => normalizeLang(browserLangRaw), [browserLangRaw]);
	const lang = useMemo(() => resolveWidgetLang(widget, browserLangRaw), [widget, browserLangRaw]);
	const isRtl = RTL_LANGS.has(lang);

	const overridesForLang = useMemo(() => {
		const root = widget?.meta?.localization?.overrides;
		const langOverrides = isPlainObject(root) ? root?.[lang] : undefined;
		return isPlainObject(langOverrides) ? langOverrides : {};
	}, [widget?.meta?.localization?.overrides, lang]);

	const msg = useCallback(
		(input, fallback, vars) => {
			const path = String(input ?? "").trim();
			if (!path) return "";

			const dot = path.indexOf(".");
			const section = dot > 0 ? path.slice(0, dot) : "";
			const key = dot > 0 ? path.slice(dot + 1) : "";

			const v1 = getStr(overridesForLang, section, key);
			if (v1 !== undefined) return formatVars(v1, vars);

			const v2 = getStr(defaultTranslations?.[lang], section, key);
			if (v2 !== undefined) return formatVars(v2, vars);

			const v3 = getStr(defaultTranslations?.en, section, key);
			if (v3 !== undefined) return formatVars(v3, vars);

			if (fallback !== null && fallback !== undefined) return formatVars(String(fallback), vars);
			return path;
		},
		[lang, overridesForLang]
	);

	return { msg, lang, isRtl, browserLang };
}
